import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://nds-pipeline.orangewater-5df01598.eastus.azurecontainerapps.io/api';

const CATEGORY_LABELS = {
  dso: 'DSO News', clinical: 'Clinical', policy: 'Policy', business: 'Business',
  technology: 'Technology', healthit: 'Health IT'
};

export default function InsightsPage() {
  const [feed, setFeed] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/content/generated?status=published`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/content/news/public`).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([posts, news]) => {

      const CO_LABELS = { nds: 'NDS', xentrios: 'Xentrios', revtech: 'RevTech', all: 'NDS' };
      const ownItems = posts.map(p => ({
        id: p._id, type: 'article',
        title: p.content?.headline || p.content?.subject || p.articleTitle || 'Untitled',
        excerpt: (p.content?.post || p.content?.plainText || '').substring(0, 200),
        date: p.publishedAt || p.createdAt,
        link: null, isInternal: true,
        category: 'article', source: CO_LABELS[p.company] || 'NDS',
        company: p.company || 'nds',
      }));

      const newsItems = news.map(n => ({
        id: n._id, type: 'news',
        title: n.title,
        excerpt: (n.description || '').replace(/<[^>]+>/g, '').substring(0, 200),
        date: n.pubDate, link: n.link, isInternal: false,
        category: n.category, source: n.source,
      }));

      setFeed([...ownItems, ...newsItems].sort((a, b) => new Date(b.date) - new Date(a.date)));
    });
  }, []);

  const filtered = filter === 'all' ? feed
    : filter === 'xentrios' ? feed.filter(f => f.type === 'xentrios')
    : feed.filter(f => f.category === filter);

  const categories = ['all', 'xentrios', ...new Set(feed.filter(f => f.type === 'news').map(f => f.category))];

  return (
    <div>
      <Helmet><title>Insights — Dental PMS, DSO Technology & AI | Xentrios</title></Helmet>

      <section className="xen-hero" style={{ padding: '4rem 0 3rem' }}>
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Insights</p>
          <h1>Dental technology news<br />and Xentrios perspectives.</h1>
          <p className="subtitle">
            Industry updates and our take on dental PMS, DSO technology, AI in healthcare,
            and revenue cycle management.
          </p>
        </div>
      </section>

      <section className="xen-section">
        <div className="xen-container">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setFilter(cat)}
                style={{
                  padding: '0.4rem 1rem', borderRadius: 20, border: '1px solid #e0e1eb',
                  background: filter === cat ? 'var(--xen-accent)' : '#fff',
                  color: filter === cat ? '#fff' : '#555',
                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, fontFamily: 'inherit',
                }}>
                {cat === 'all' ? 'All' : cat === 'xentrios' ? 'Xentrios' : CATEGORY_LABELS[cat] || cat}
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 0', color: '#888' }}>
              <p>No content yet. Check back soon.</p>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {filtered.map(item => (
              <div key={item.id} style={{
                background: '#fff', border: '1px solid #e8e9f0', borderRadius: 12, padding: '1.5rem',
                borderLeft: item.type === 'xentrios' ? '4px solid var(--xen-accent)' : '4px solid #e8e9f0',
                transition: 'box-shadow 0.2s', cursor: 'pointer',
              }}
                onClick={() => item.link && window.open(item.link, '_blank')}
              >
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 4,
                    background: item.type === 'xentrios' ? 'var(--xen-accent)' : '#f4f5fa',
                    color: item.type === 'xentrios' ? '#fff' : '#555',
                  }}>{item.source}</span>
                  <span style={{ fontSize: '0.75rem', color: '#999' }}>{dayjs(item.date).format('MMM D, YYYY')}</span>
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', lineHeight: 1.3 }}>{item.title}</h3>
                <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.6 }}>{item.excerpt}...</p>
                {item.link && <span style={{ fontSize: '0.75rem', color: 'var(--xen-accent)', marginTop: '0.5rem', display: 'block' }}>Read on {item.source} →</span>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
